import { FileIcon, UploadSimpleIcon, XIcon } from '@phosphor-icons/react';
import classNames from '@utils/classnames';
import React, { useRef, useState } from 'react';
import { useTranslation } from '../contexts/translation-context';
import { Button } from './button/Button';

export interface IDropzoneProps {
  files: globalThis.File[];
  onUpload: (files: globalThis.File[]) => any;
  isDisabled?: boolean;
  accept?: string;
  allowMultiple?: boolean;
}

export const Dropzone = (props: IDropzoneProps) => {
  let { files, onUpload, isDisabled: disabled, accept, allowMultiple } = props;
  let inputEl = useRef<HTMLInputElement | null>(null);
  let [dragging, setDragging] = useState<boolean>(false);
  const { i18n } = useTranslation();

  const handleUpload = (inputFiles: globalThis.File[]) => {
    if (allowMultiple) {
      onUpload([...files, ...inputFiles]);
    } else {
      onUpload(inputFiles[0] ? [inputFiles[0]] : []);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;

    let inputFiles = e.target.files;
    if (!inputFiles) {
      return;
    }

    handleUpload([...inputFiles]);
  };

  const handleClick = () => {
    if (disabled) return;

    if (inputEl.current) {
      // @ts-ignore
      // eslint-disable-next-line no-restricted-globals
      inputEl.current.dispatchEvent(new MouseEvent('click'));
    }
  };

  const dragEnter = (e: React.DragEvent<HTMLInputElement>) => {
    if (disabled) return;
    e.stopPropagation();
    e.preventDefault();

    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragging(true);
    }
  };

  const dragOver = (e: React.DragEvent<HTMLInputElement>) => {
    if (disabled) return;
    e.stopPropagation();
    e.preventDefault();
  };

  const dragLeave = (e: React.DragEvent<HTMLInputElement>) => {
    if (disabled) return;
    e.stopPropagation();
    e.preventDefault();

    setDragging(false);
  };

  const drop = (e: React.DragEvent<HTMLInputElement>) => {
    if (disabled) return;
    e.stopPropagation();
    e.preventDefault();

    handleUpload([...e.dataTransfer.files]);
  };

  return (
    <div
      className={classNames(
        'w-full rounded-lg border border-dashed bg-surface-sunken text-fg-muted transition-colors',
        {
          'cursor-pointer hover:border-border-strong hover:bg-surface': !disabled,
          'border-brand bg-brand-subtle': dragging,
          'border-border': !dragging,
        },
      )}
      onClick={handleClick}
    >
      <div
        className="min-h-40 max-h-[50vh] w-full overflow-y-auto"
        onDragEnter={dragEnter}
        onDragOver={dragOver}
        onDragLeave={dragLeave}
        onDrop={drop}
      >
        {files.length ? (
          <div className="w-full">
            {files.map((file, idx) => {
              return (
                <div
                  className={classNames('flex items-center gap-3 px-3 py-2 text-fg', {
                    'border-b border-border': idx !== files.length - 1,
                  })}
                  key={`file-${file.name}-${idx}`}
                >
                  <FileIcon className="h-5 w-5 shrink-0 text-fg-muted" />
                  <div className="min-w-0 flex-1 truncate text-sm">{file.name}</div>
                  <Button
                    size={8}
                    color="ghost"
                    ariaLabel="Verwijder"
                    onTrigger={(evt) => {
                      evt.preventDefault();
                      evt.stopPropagation();

                      const clonedArr = [...files];
                      clonedArr.splice(idx, 1);
                      onUpload(clonedArr);
                    }}
                  >
                    <XIcon className="button-icon" />
                  </Button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex min-h-40 w-full flex-col items-center justify-center gap-2 p-6 text-center">
            <UploadSimpleIcon className="h-6 w-6" />
            <div className="text-xs">{i18n('upload.dropzoneHint')}</div>
          </div>
        )}
      </div>
      <input
        type="file"
        multiple={Boolean(allowMultiple)}
        accept={accept}
        onChange={handleFileUpload}
        className="hidden"
        ref={inputEl}
      ></input>
    </div>
  );
};
